import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, ButtonGroup, Menu, MenuItem, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import {
  Edit,
  Print,
  Delete,
  Block,
  SaveAlt,
  ArrowDropDown,
  ExitToApp,
  RestartAlt,
  Preview,
  SkipNext,
  SkipPrevious,
  Receipt,
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { handleFreeze } from '../../utils/common';
import { useSelector } from 'react-redux';
import { useThemes } from '../Theme/theme';
import { LoadingButton } from '@mui/lab';
import { useLocation } from 'react-router-dom';

const DraggableContainer = styled('div')({
  position: 'fixed',
  cursor: 'grab',
  zIndex: 1000,
  padding: '10px',
  borderRadius: '4px',
});

const DraggableAction = ({
  from,
  onKeyPress,
  saveText,
  actionType,
  onEInvoice,
  onSave,
  editMode,
  onSavePrint,
  onPrint,
  id,
  stat,
  onSaveNew,
  onDelete,
  onExit,
  hideFloatBtn,
  onReset,
  type,
  loading,
  onCancel,
  onEnableEdit,
  onPreview,
  onFinish,
  onSkip,
  onPrevs,
  textToCancel,
}) => {

  const Freeze = useSelector((state) => state.userPrefrenceandReducer?.financialYear?.freezestatus);
  if (Freeze == 1 && actionType !== 'CompanySetUp') {
    onEnableEdit = onEnableEdit ? handleFreeze : undefined;
    onSave = onSave ? handleFreeze : undefined;
    onSaveNew = onSaveNew ? handleFreeze : undefined;
    onDelete = onDelete ? handleFreeze : undefined;
    onCancel = onCancel ? handleFreeze : undefined;
    onEInvoice = onEInvoice ? handleFreeze : undefined;
  }
  const location = useLocation();
  const { theme } = useThemes();
  const [position, setPosition] = useState({ x: 0, y: 10 });
  const [dragging, setDragging] = useState(false);
  const [draggingTooltip, setDraggingTooltip] = useState(false);
  const actionRef = useRef(null);
  const saveAnchorRef = React.useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [saveAnchorEl, setSaveAnchorEl] = useState(null);
  const saveColor = "#ee5b2b"

  const [startPos, setStartPos] = useState({ x: 0, y: 0 }); // Initial mouse down position
  const textBack =
    type && (location.pathname.includes('invoicetax') || location.pathname.includes('purchasetax')) ? 'Back to invoice' : null;
  const handleMouseMove = (event) => {
    // Only set dragging to true if there's actual movement
    const distanceX = Math.abs(event.clientX - startPos.x);
    const distanceY = Math.abs(event.clientY - startPos.y);
    const movementThreshold = 5; // You can adjust this value

    if (distanceX > movementThreshold || distanceY > movementThreshold) {
      setDragging(true); // Set dragging state to true only when actually dragging
      setDraggingTooltip(true);
    }

    setPosition((prevPosition) => {
      // Calculate new position
      const newX = prevPosition.x - (event.movementX / window.innerWidth) * 100;
      const newY = prevPosition.y - (event.movementY / window.innerHeight) * 100;

      // Convert percentage position to pixels
      const elementHeight = actionRef.current?.offsetHeight || 100; // Assuming element height is 100px
      const elementWidth = actionRef.current?.offsetWidth || 100; // Assuming element width is 100px

      const newYInPixels = (newY / 100) * window.innerHeight;
      const newXInPixels = (newX / 100) * window.innerWidth;

      // Define bounds for the drag area
      const upperBoundY = 2;
      const lowerBoundY = window.innerHeight - 50 - elementHeight;
      const leftBoundX = 0;
      const rightBoundX = window.innerWidth - 115 - elementWidth;

      // Restrict new position within boundaries
      const restrictedY = Math.max(upperBoundY, Math.min(newYInPixels, lowerBoundY));
      const restrictedX = Math.max(leftBoundX, Math.min(newXInPixels, rightBoundX));

      // Convert restricted positions back to percentages
      const restrictedYPercentage = (restrictedY / window.innerHeight) * 100;
      const restrictedXPercentage = (restrictedX / window.innerWidth) * 100;

      return {
        x: restrictedXPercentage,
        y: restrictedYPercentage,
      };
    });
  };
  const handleMouseDown = (event) => {
    setStartPos({ x: event.clientX, y: event.clientY });
    setDragging(false); // Reset dragging state
    setDraggingTooltip(false);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseUp = (event) => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    setDraggingTooltip(false);

    const distanceX = Math.abs(event.clientX - startPos.x);
    const distanceY = Math.abs(event.clientY - startPos.y);
    const movementThreshold = 5; // You can adjust this value

    if (distanceX < movementThreshold && distanceY < movementThreshold) {
      setDragging(false);
      handleMoreOptionsClick(event); // Handle click event only if it's not a drag
    } else {
      setDragging(false);
    }
  };
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging]);

  useEffect(() => {
    const handleResize = () => {
      if (actionRef.current) {
        const { innerWidth, innerHeight } = window;
        const { offsetWidth, offsetHeight } = actionRef.current;
        setPosition((prevPosition) => ({
          x: Math.min(innerWidth - offsetWidth - 15, prevPosition.x),
          y: Math.min(innerHeight - offsetHeight - 15, prevPosition.y),
        }));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMoreOptionsClick = (event) => {
    if (!dragging && event.currentTarget !== window) {
      setAnchorEl(event.currentTarget);
    } else {
      event.stopPropagation();
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSaveAnchorEl(null);
  };
  const handleMenuItemClick = (action) => {
    if (!dragging) {
      if (action == 'Skip' && onSkip !== undefined) {
        onSkip();
      }
      if (action == 'Previous' && onPrevs !== undefined) {
        onPrevs();
      }
      if (action == 'cancel invoice' && onCancel !== undefined) {
        onCancel();
      }
      if (action == 'Listing' && onExit !== undefined) {
        onExit();
      }
      if (action == 'Reset' && onReset !== undefined) {
        onReset();
      }
      if (action == 'save and new' && onSaveNew !== undefined) {
        onSaveNew();
      }
      if (action == 'save and print' && onSavePrint !== undefined) {
        onSavePrint();
      }
      if (action == 'Preview' && onPreview !== undefined) {
        onPreview();
      }
      if (action == 'print' && onPrint !== undefined) {
        onPrint();
      }
      if (action == 'delete' && onDelete !== undefined) {
        onDelete();
      }
      if (action == 'EInvoicing' && onEInvoice !== undefined) {
        onEInvoice();
      }

      if (action == 'save' || action == 'update') {
        if (onSave == undefined && onEnableEdit !== undefined) {
          onEnableEdit();
        } else if (action == 'save' && onSave !== undefined) {
          onSave();
        } else if (action == 'update' && onSave !== undefined) {
          onSave();
        }
      }
      handleMenuClose();
    }
  };

  const handleSaveOptionsClick = (event) => {
    if (!dragging) {
      setSaveAnchorEl(event.currentTarget);
    } else {
      event.stopPropagation();
    }
  };
  let cancelInvoiceText = () => {
    if (from == 'credit') {
      return 'Cancel Cr. Note';
    } else if (from == 'debit') {
      return 'Cancel Dr. Note';
    } else if (from == 'receipt' || from == 'payment') {
      return 'Cancel ' + from.charAt(0).toUpperCase() + from.slice(1);
    } else if (from == 'JE') {
      return 'Cancel Journal';
    } else if (stat == 'active' || stat == 'inactive') {
      return `Mark as ${stat}`;
    } else {
      return 'Cancel Invoice';
    }
  };
  const isSmallDvce = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      {hideFloatBtn === true ? null : (
        <DraggableContainer
          ref={actionRef}
          style={{
            right: `${position.x}%`,
            bottom: isSmallDvce ? '7%' : `${position.y}%`,
          }}
          onMouseDown={handleMouseDown}
        >
          <Box>
            {actionType === 'save' && (
              <Box sx={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                {onSavePrint || onPrint || onExit || onSaveNew || onPreview || onReset ? (
                  <ButtonGroup 
                    variant="contained" 
                    ref={saveAnchorRef} 
                    aria-label="Button group with a nested menu"
                    sx={{
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        boxShadow: 'none',
                      },
                      '& .MuiButtonGroup-grouped:not(:first-of-type)': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Tooltip arrow>
                      <LoadingButton
                        id="SaveChanges"
                        endIcon={onSave == undefined && stat !== 'active' && stat !== '' ? <Edit /> : null}
                        sx={{
                          // background: 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                          background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                          position: 'relative',
                          '&:hover': {
                            background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                          },
                        }}
                        onClick={() => {
                          handleMenuItemClick('save');
                        }}
                        loading={id ? loading : false}
                      >
                        {onSave == undefined
                          ? stat !== 'active' && stat !== ''
                            ? 'Enable Edit'
                            : 'Mark as active'
                          : saveText || (
                              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                                <Typography>Save</Typography>
                                <Typography sx={{ fontSize: '8px', display: isSmallDvce ? 'none' : 'block' }}>( CTRL+S )</Typography>{' '}
                              </Box>
                            )}
                      </LoadingButton>
                    </Tooltip>
                    <Button
                    sx={{
                      background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                      '&:hover': {
                        background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                      },
                    }}
                      size="small"
                      onClick={handleSaveOptionsClick}
                    >
                      <ArrowDropDown />
                    </Button>
                  </ButtonGroup>
                ) : (
                  <LoadingButton
                    endIcon={onSave == undefined && stat !== 'active' && stat !== '' ? <Edit /> : null}
                    sx={{
                      background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                      '&:hover': {
                        background: onSave == undefined ? 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)' : '#ee5b2b',
                      },
                    }}
                    variant="contained"
                    onClick={() => {
                      handleMenuItemClick('save');
                    }}
                    loading={loading}
                  >
                    {onSave == undefined ? (
                      stat !== 'active' && stat !== '' ? (
                        'Enable Edit'
                      ) : (
                        'Mark as active'
                      )
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Typography>Save</Typography>
                        <Typography sx={{ fontSize: '8px', display: isSmallDvce ? 'none' : 'block' }}>( CTRL+S )</Typography>{' '}
                      </Box>
                    )}
                  </LoadingButton>
                )}
                <Menu
                  id="save-options-menu"
                  anchorEl={saveAnchorEl}
                  open={Boolean(saveAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'bottom' : 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'top' : 'bottom',
                    horizontal: 'left',
                  }}
                  sx={{
                    '& .MuiPaper-root': {
                      background: theme.palette.mode == 'light' ? '' : '#1E1E1E',
                    },
                  }}
                >
                  {onSavePrint ? (
                    <MenuItem onClick={() => handleMenuItemClick('save and print')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Print />}>
                        Save & Print
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onSaveNew ? (
                    <MenuItem onClick={() => handleMenuItemClick('save and new')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<SaveAlt />}>
                        Save & New
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onPrint ? (
                    <MenuItem onClick={() => handleMenuItemClick('print')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Print />}>
                        Print
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onPreview ? (
                    <MenuItem onClick={() => handleMenuItemClick('Preview')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Preview />}>
                        Preview
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onReset ? (
                    <MenuItem onClick={() => handleMenuItemClick('Reset')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<RestartAlt />}>
                        Reset
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onExit ? (
                    <MenuItem onClick={() => handleMenuItemClick('Listing')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<ExitToApp />}>
                        {textToCancel ? textToCancel : textBack ? textBack : 'Back to Listing'}
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onDelete ? (
                    <MenuItem onClick={() => handleMenuItemClick('delete')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Delete />}>
                        Delete
                      </Button>
                    </MenuItem>
                  ) : null}
                  {console.log(stat, 'stat')}
                  {onCancel && stat !== 'active' ? (
                    <MenuItem onClick={() => handleMenuItemClick('cancel invoice')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Block />}>
                        {/* Cancel Invoice */}
                        {cancelInvoiceText()}
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onEInvoice ? (
                    <MenuItem onClick={() => handleMenuItemClick('EInvoicing')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Receipt />}>
                        Generate E-Invoice
                      </Button>
                    </MenuItem>
                  ) : null}
                </Menu>
              </Box>
            )}
            {actionType === 'edit' && (
              <Box sx={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                {onCancel || onPrint || onDelete || onExit || onPreview ? (
                  <ButtonGroup 
                    variant="contained" 
                    ref={saveAnchorRef} 
                    aria-label="Button group with a nested menu"
                    sx={{
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        boxShadow: 'none',
                      },
                      '& .MuiButtonGroup-grouped:not(:first-of-type)': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Tooltip arrow>
                      <Button
                        sx={{
                          background: '#ee5b2b',
                          '&:hover': {
                            background: '#ee5b2b',
                          },
                        }}
                        onClick={() => {
                          handleMenuItemClick('update');
                        }}
                      >
                        {saveText || (
                          <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                            <Typography>Update</Typography>
                            <Typography sx={{ fontSize: '8px', display: isSmallDvce ? 'none' : 'block' }}>( CTRL+S )</Typography>{' '}
                          </Box>
                        )}
                      </Button>
                    </Tooltip>

                    <Button
                      sx={{
                         background: '#ee5b2b',
                          '&:hover': {
                            background: '#ee5b2b',
                          },
                      }}
                      size="small"
                      onClick={handleMoreOptionsClick}
                    >
                      <ArrowDropDown />
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Button
                    sx={{
                      background: '#ee5b2b',
                      '&:hover': {
                        background: '#ee5b2b',
                      },
                    }}
                    variant="contained"
                    onClick={() => {
                      handleMenuItemClick('update');
                    }}
                  >
                    {saveText || (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Typography>Update</Typography>
                        <Typography sx={{ fontSize: '8px', display: isSmallDvce ? 'none' : 'block' }}>( CTRL+S )</Typography>{' '}
                      </Box>
                    )}
                  </Button>
                )}
                <Menu
                  id="more-options-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'bottom' : 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'top' : 'bottom',
                    horizontal: 'left',
                  }}
                >
                  {onPrint ? (
                    <MenuItem onClick={() => handleMenuItemClick('print')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Print />}>
                        Print
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onPreview ? (
                    <MenuItem onClick={() => handleMenuItemClick('Preview')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Preview />}>
                        Preview
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onDelete ? (
                    <MenuItem onClick={() => handleMenuItemClick('delete')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Delete />}>
                        Delete
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onCancel && stat !== 'active' ? (
                    <MenuItem onClick={() => handleMenuItemClick('cancel invoice')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Block />}>
                        {/* Cancel Invoice */}
                        {cancelInvoiceText()}
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onExit ? (
                    <MenuItem onClick={() => handleMenuItemClick('Listing')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<ExitToApp />}>
                        {textToCancel ? textToCancel : textBack ? textBack : 'Back to Listing'}
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onEInvoice ? (
                    <MenuItem onClick={() => handleMenuItemClick('EInvoicing')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Receipt />}>
                        Generate E-Invoice
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onReset ? (
                    <MenuItem onClick={() => handleMenuItemClick('Reset')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<RestartAlt />}>
                        Reset
                      </Button>
                    </MenuItem>
                  ) : null}
                </Menu>
              </Box>
            )}
            {actionType === 'CompanySetUp' && (
              <Box sx={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                {onSave || onSkip || onPrevs || onFinish || onEnableEdit ? (
                  <ButtonGroup 
                    variant="contained" 
                    ref={saveAnchorRef} 
                    aria-label="Button group with a nested menu"
                    sx={{
                      '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                        boxShadow: 'none',
                      },
                      '& .MuiButtonGroup-grouped:not(:first-of-type)': {
                        boxShadow: 'none',
                      },
                    }}
                  >
                    <Tooltip
                      arrow
                      title={
                        saveText == 'Back to Dashboard' ? (
                          ''
                        ) : draggingTooltip == false ? (
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2" sx={{ fontSize: '11px' }}>
                              Press <kbd>ctrl</kbd> + <kbd>s</kbd>
                            </Typography>
                          </Box>
                        ) : null
                      }
                    >
                      <Button
                        sx={{
                          background: saveText === 'Save & Next' ? saveColor : 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                         '&:hover': {
                       background: saveText === 'Save & Next' ? saveColor : 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                         },
                        }}
                        onClick={() => {
                          if (onEnableEdit && editMode === false) {
                            onEnableEdit();
                          } else {
                            handleMenuItemClick('save');
                          }
                        }}
                      >
                        {saveText}
                      </Button>
                    </Tooltip>
                    <Button
                      sx={{
                         background: saveText === 'Save & Next' ? saveColor : 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                         '&:hover': {
                       background: saveText === 'Save & Next' ? saveColor : 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                         },
                      }}
                      size="small"
                      onClick={handleSaveOptionsClick}
                      onKeyDown={(e) => onKeyPress(e)}
                    >
                      <ArrowDropDown />
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Button
                    sx={{
                      background: 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                    }}
                    variant="contained"
                    onKeyDown={() => onKeyPress()}
                    onClick={() => {
                      handleMenuItemClick('save');
                    }}
                  >
                    {saveText}
                  </Button>
                )}
                <Menu
                  id="save-options-menu"
                  anchorEl={saveAnchorEl}
                  open={Boolean(saveAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'bottom' : 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'top' : 'bottom',
                    horizontal: 'left',
                  }}
                >
                  {onSkip ? (
                    <MenuItem onClick={() => handleMenuItemClick('Skip')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<SkipNext />}>
                        Skip
                      </Button>
                    </MenuItem>
                  ) : null}
                  {onPrevs ? (
                    <MenuItem onClick={() => handleMenuItemClick('Previous')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<SkipPrevious />}>
                        Previous
                      </Button>
                    </MenuItem>
                  ) : null}
                </Menu>
              </Box>
            )}
            {actionType === 'cancelled' && (
              <Box sx={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                <ButtonGroup 
                  variant="contained" 
                  ref={saveAnchorRef} 
                  aria-label="Button group with a nested menu"
                  sx={{
                    '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                      boxShadow: 'none',
                    },
                    '& .MuiButtonGroup-grouped:not(:first-of-type)': {
                      boxShadow: 'none',
                    },
                  }}
                >
                  <Button
                    sx={{
                      background: 'linear-gradient(180deg, #FD5380 0%, #D91C80 100%)',
                      color: 'white',
                    }}
                    onClick={() => {
                      onExit();
                    }}
                  >
                    BACK TO LISTING
                  </Button>
                  <Button
                      sx={{
                        background: 'linear-gradient(82deg, rgba(217,28,128,1) 0%, rgba(97,16,59,1) 100%)',
                      }}
                      size="small"
                      onClick={handleSaveOptionsClick}
                    >
                      <ArrowDropDown />
                  </Button>
                </ButtonGroup>
                <Menu
                  id="save-options-menu"
                  anchorEl={saveAnchorEl}
                  open={Boolean(saveAnchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'bottom' : 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: position.y > window.innerHeight / 2 ? 'top' : 'bottom',
                    horizontal: 'left',
                  }}
                  sx={{
                    '& .MuiPaper-root': {
                      background: theme.palette.mode == 'light' ? '' : '#1E1E1E',
                    },
                  }}
                >
                 
                  {onDelete ? (
                    <MenuItem onClick={() => handleMenuItemClick('delete')}>
                      <Button sx={{ p: 0, fontSize: '14px', fontWeight: 800 }} startIcon={<Delete />}>
                        Delete
                      </Button>
                    </MenuItem>
                  ) : null}
            
                </Menu>
              </Box>
            )}
          </Box>
        </DraggableContainer>
      )}
    </>
  );
};

DraggableAction.propTypes = {
  actionType: PropTypes.oneOf(['edit', 'save', 'CompanySetUp']).isRequired,
};

export default DraggableAction;
